import { env } from '../config/env';

interface GoogleTokenResponse {
  access_token?: string;
  error?: string;
  error_description?: string;
}

interface GoogleTokenClient {
  requestAccessToken: () => void;
}

interface GoogleErrorCallbackPayload {
  type: string;
  message?: string;
}

declare global {
  interface Window {
    google?: {
      accounts: {
        oauth2: {
          initTokenClient: (config: {
            client_id: string;
            scope: string;
            callback: (response: GoogleTokenResponse) => void;
            error_callback?: (error: GoogleErrorCallbackPayload) => void;
          }) => GoogleTokenClient;
        };
      };
    };
  }
}

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

let scriptLoadPromise: Promise<void> | null = null;

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.oauth2) return Promise.resolve();
  if (!scriptLoadPromise) {
    scriptLoadPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = GIS_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Identity Services.'));
      document.head.appendChild(script);
    });
  }
  return scriptLoadPromise;
}

// Deliberately NOT Firebase's signInWithPopup/signInWithRedirect — see AuthContext.tsx's
// signInWithGoogle for why both proved unreliable in production (popup needs third-party
// storage access Chrome's default partitioning blocks; redirect's getRedirectResult()
// silently lost the credential on the multi-origin round trip). GIS's popup and its
// postMessage relay back to this page are Google's own, so they never touch Firebase's
// iframe-based relay that's the actual root cause of both failures.
export async function requestGoogleAccessToken(): Promise<string> {
  if (!env.googleClientId) {
    throw new Error('Google sign-in is not configured.');
  }
  await loadGisScript();

  return new Promise<string>((resolve, reject) => {
    const client = window.google!.accounts.oauth2.initTokenClient({
      client_id: env.googleClientId!,
      scope: 'openid email profile',
      callback: (response) => {
        if (response.error || !response.access_token) {
          reject(new Error(response.error_description || response.error || 'Google sign-in failed.'));
          return;
        }
        resolve(response.access_token);
      },
      error_callback: (error) => {
        reject(new Error(error.message || `Google sign-in failed (${error.type}).`));
      },
    });
    // Must run inside the click handler that called requestGoogleAccessToken() — the popup
    // this opens is only allowed by the browser as a direct result of a user gesture.
    client.requestAccessToken();
  });
}
